import { useEffect } from 'react';
import { styled } from '@mui/material';
import Typography from '@mui/material/Typography';
import { Link, RouteComponentProps } from '@reach/router';
import { useLazyQuery } from '@apollo/client';
import { useAuth } from '@calefy-inc/authentication';
import { DisplayInlineError } from '../../common/ErrorHandling';
import LoadingScreen from '../../ManagementPanel/components/LoadingScreen';
import { AdminPolicyStreamPlansTable } from './AdminPolicyStreamPlansTable';
import { graphql } from '../../../gql';
import { formatDateTimeForDisplay } from '../../../util';

const planDetailPath = (planId: string) => {
  return `/insurtech/admin/policystream/plans/${planId}`;
};

interface CompulifeApiCallDetailAdminPageProps
  extends RouteComponentProps<{ callId: string }> {}

export const CompulifeApiCallDetailAdminPage = ({
  callId,
}: CompulifeApiCallDetailAdminPageProps) => {
  const { token } = useAuth();
  const [runQuery, { data, loading, error }] = useLazyQuery(
    ADMIN_COMPULIFE_API_CALL_RECORD_BY_ID,
  );

  useEffect(() => {
    if (token && callId) {
      runQuery({ variables: { token, id: callId } });
    }
  }, [token, callId, runQuery]);

  const record = data?.compulifeApiCallRecordById;

  if (!callId) {
    return (
      <PageSection>
        <DisplayInlineError error={new Error('Missing call id.')} />
      </PageSection>
    );
  }

  if (!token) {
    return (
      <PageSection>
        <Typography>Sign in to view this call.</Typography>
      </PageSection>
    );
  }

  return (
    <PageSection>
      <Typography variant='h5' component='h1' gutterBottom>
        Compulife API call {record?.id ?? callId}
      </Typography>
      <BackLink to='/insurtech/admin/policystream/compulifecalls'>
        &larr; All Compulife calls
      </BackLink>

      <DisplayInlineError error={error} />

      {loading && !data ? <LoadingScreen /> : null}

      {!loading && data && !record ? (
        <Typography>Call not found.</Typography>
      ) : null}

      {record ? (
        <>
          <DetailGrid>
            <DetailTerm>id</DetailTerm>
            <DetailDef>{record.id}</DetailDef>

            <DetailTerm>createdAt</DetailTerm>
            <DetailDef>{formatDateTimeForDisplay(record.createdAt)}</DetailDef>

            <DetailTerm>birthDay / birthMonth / birthYear</DetailTerm>
            <DetailDef>
              {record.birthMonth}/{record.birthDay}/{record.birthYear}
            </DetailDef>

            <DetailTerm>sex</DetailTerm>
            <DetailDef>{record.sex}</DetailDef>

            <DetailTerm>smoker</DetailTerm>
            <DetailDef>{record.smoker}</DetailDef>

            <DetailTerm>health</DetailTerm>
            <DetailDef>{record.health}</DetailDef>

            <DetailTerm>faceAmount</DetailTerm>
            <DetailDef>{record.faceAmount}</DetailDef>

            <DetailTerm>state</DetailTerm>
            <DetailDef>{record.state}</DetailDef>

            <DetailTerm>userLocation</DetailTerm>
            <DetailDef>{record.userLocation}</DetailDef>

            <DetailTerm>compulifeLanguage</DetailTerm>
            <DetailDef>{record.compulifeLanguage}</DetailDef>

            <DetailTerm>modeUsed</DetailTerm>
            <DetailDef>{record.modeUsed}</DetailDef>

            <DetailTerm>compinc</DetailTerm>
            <DetailDef>{record.compinc}</DetailDef>

            <DetailTerm>proddis</DetailTerm>
            <DetailDef>{record.proddis}</DetailDef>

            <DetailTerm>newCategory</DetailTerm>
            <DetailDef>{record.newCategory ?? '—'}</DetailDef>

            <DetailTerm>sortOverride1</DetailTerm>
            <DetailDef>{record.sortOverride1}</DetailDef>

            <DetailTerm>remoteIp</DetailTerm>
            <DetailDef>{record.remoteIp ?? '—'}</DetailDef>

            <DetailTerm>spoofedIp</DetailTerm>
            <DetailDef>{record.spoofedIp ?? '—'}</DetailDef>

            <DetailTerm>requestUrl</DetailTerm>
            <RequestUrlDef>{record.requestUrl}</RequestUrlDef>

            <DetailTerm>responseStatusCode</DetailTerm>
            <DetailDef>
              {record.responseStatusCode !== null &&
              record.responseStatusCode !== undefined
                ? String(record.responseStatusCode)
                : '—'}
            </DetailDef>

            <DetailTerm>responseText</DetailTerm>
            <ResponseTextBlock>{record.responseText}</ResponseTextBlock>
          </DetailGrid>

          <PlansSubheading>PolicyStream plans from this call</PlansSubheading>
          <AdminPolicyStreamPlansTable
            plans={record.policyStreamPlans}
            planDetailPathForId={planDetailPath}
          />
        </>
      ) : null}
    </PageSection>
  );
};

const PageSection = styled('section')(({ theme }) => {
  return { paddingTop: theme.spacing(2) };
});

const PlansSubheading = styled('h2')(({ theme }) => {
  return {
    ...theme.typography.h6,
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(1),
  };
});

const BackLink = styled(Link)(({ theme }) => {
  return {
    display: 'inline-block',
    marginBottom: theme.spacing(2),
  };
});

const DetailGrid = styled('dl')(({ theme }) => {
  return {
    display: 'grid',
    gridTemplateColumns: 'max-content 1fr',
    gap: theme.spacing(1, 3),
    alignItems: 'start',
    maxWidth: 960,
  };
});

const DetailTerm = styled('dt')(({ theme }) => {
  return {
    margin: 0,
    fontWeight: 600,
    color: theme.palette.text.secondary,
  };
});

const DetailDef = styled('dd')(() => {
  return { margin: 0 };
});

const RequestUrlDef = styled('dd')(({ theme }) => {
  return {
    margin: 0,
    wordBreak: 'break-all',
    fontFamily: 'monospace',
    fontSize: theme.typography.body2.fontSize,
  };
});

const ResponseTextBlock = styled('dd')(({ theme }) => {
  return {
    margin: 0,
    gridColumn: '1 / -1',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    fontFamily: 'monospace',
    fontSize: theme.typography.body2.fontSize,
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    maxHeight: 360,
    overflow: 'auto',
  };
});

const ADMIN_COMPULIFE_API_CALL_RECORD_BY_ID = graphql(`
  query AdminCompulifeApiCallRecordById($token: String!, $id: ID!) {
    compulifeApiCallRecordById(token: $token, id: $id) {
      id
      createdAt
      birthDay
      birthMonth
      birthYear
      compinc
      compulifeLanguage
      faceAmount
      health
      modeUsed
      newCategory
      proddis
      remoteIp
      spoofedIp
      requestUrl
      responseStatusCode
      responseText
      sex
      smoker
      sortOverride1
      state
      userLocation
      policyStreamPlans {
        ...AdminPolicyStreamPlanTableFields
      }
    }
  }
`);
